<script setup lang="ts">
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { ChevronDownIcon } from '@heroicons/vue/20/solid'
  import { Teleport } from 'vue'
  import type { StateEnum } from '@/types/generated/types.gen'
  import {
    LucideClipboardCheck,
    LucideClipboardList,
    LucideClipboardPlus,
    LucideClipboardX
  } from 'lucide-vue-next'
  import Button from '@/components/ui/button/Button.vue'

  const props = defineProps<{
    modelValue: StateEnum
    id: string
    compact?: boolean // New prop for compact mobile display
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: StateEnum): void
  }>()

  // Global state for managing open dropdowns
  const openDropdownId = ref<string | null>(null)
  const isOpen = computed(() => openDropdownId.value === props.id)

  const buttonRef = ref<HTMLElement>()
  const dropdownPosition = ref({ x: 0, y: 0, isAbove: false, width: 0 })

  const updateDropdownPosition = () => {
    if (!buttonRef.value) return
    const rect = buttonRef.value.getBoundingClientRect()
    const dropdownHeight = 200
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - rect.bottom
    const shouldShowAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight

    // Calculate position relative to the viewport
    const x = Math.max(0, Math.min(rect.left, window.innerWidth - 200)) // Ensure dropdown doesn't go off-screen horizontally
    const y = shouldShowAbove ? rect.top - dropdownHeight : rect.bottom
    const width = Math.max(rect.width, 200) // Make dropdown at least as wide as the button

    dropdownPosition.value = {
      x,
      y,
      isAbove: shouldShowAbove,
      width
    }
  }

  onMounted(() => {
    window.addEventListener('scroll', updateDropdownPosition, true)
    window.addEventListener('resize', updateDropdownPosition)

    // Global click handler
    document.addEventListener('click', (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.status-select-container')) {
        closeDropdown()
      }
    })
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', updateDropdownPosition, true)
    window.removeEventListener('resize', updateDropdownPosition)
  })

  const openDropdown = (e: MouseEvent) => {
    e.stopPropagation() // Prevent event bubbling
    if (openDropdownId.value && openDropdownId.value !== props.id) {
      openDropdownId.value = null // Close other dropdowns
    }
    openDropdownId.value = openDropdownId.value === props.id ? null : props.id
    if (openDropdownId.value) {
      updateDropdownPosition()
    }
  }

  const closeDropdown = () => {
    if (openDropdownId.value === props.id) {
      openDropdownId.value = null
    }
  }

  const { t } = useTranslation({
    en: {
      newInquiry: 'Neue Anfrage',
      inProgress: 'In Bearbeitung',
      completed: 'Abgeschlossen',
      rejected: 'Abgelehnt'
    },
    de: {
      newInquiry: 'Neue Anfrage',
      inProgress: 'In Bearbeitung',
      completed: 'Abgeschlossen',
      rejected: 'Abgelehnt'
    }
  })

  const statusOptions = computed(() => [
    {
      value: 'NEW' as StateEnum,
      label: t('newInquiry'),
      shortLabel: 'Neu', // Short label for mobile
      icon: LucideClipboardPlus,
      buttonClass:
        'bg-yellow-100 text-yellow-600 border border-yellow-200 font-normal hover:bg-yellow-200 hover:text-yellow-700',
      iconClass: 'text-yellow-600',
      badgeClass: 'bg-yellow-50 text-yellow-700 border border-yellow-300'
    },
    {
      value: 'IN_PROGRESS' as StateEnum,
      label: t('inProgress'),
      shortLabel: 'Aktiv', // Short label for mobile
      icon: LucideClipboardList,
      buttonClass:
        'bg-blue-100 text-blue-600 border border-blue-200 font-normal hover:bg-blue-200 hover:text-blue-700',
      iconClass: 'text-blue-600',
      badgeClass: 'bg-blue-50 text-blue-700 border border-blue-300'
    },
    {
      value: 'COMPLETED' as StateEnum,
      label: t('completed'),
      shortLabel: 'Beendet', // Short label for mobile
      icon: LucideClipboardCheck,
      buttonClass:
        'bg-green-100 text-green-600 border border-green-200 font-normal hover:bg-green-200 hover:text-green-700',
      iconClass: 'text-green-600',
      badgeClass: 'bg-green-50 text-green-700 border border-green-300'
    },
    {
      value: 'REJECTED' as StateEnum,
      label: t('rejected'),
      shortLabel: 'Abgelehnt', // Short label for mobile
      icon: LucideClipboardX,
      buttonClass:
        'bg-red-100 text-red-600 border border-red-200 font-normal hover:bg-red-200 hover:text-red-700',
      iconClass: 'text-red-600',
      badgeClass: 'bg-red-50 text-red-700 border border-red-300'
    }
  ])

  const currentStatus = computed(() => {
    const option = statusOptions.value.find((opt) => opt.value === props.modelValue)
    return option || statusOptions.value[0] // Default to NEW if no match
  })

  const handleStatusChange = (newStatus: StateEnum) => {
    emit('update:modelValue', newStatus)
    closeDropdown()
  }
</script>

<template>
  <div class="status-select-container relative">
    <button
      ref="buttonRef"
      type="button"
      class="inline-flex h-7 w-full items-center justify-between rounded-md px-2 py-1.5 text-sm font-medium
        shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
        focus-visible:ring-offset-2 sm:px-3 sm:py-2"
      :class="currentStatus.buttonClass"
      @click="openDropdown"
    >
      <div class="flex items-center truncate">
        <span
          class="mr-1"
          :class="currentStatus.iconClass"
        >
          <component
            :is="currentStatus.icon"
            class="h-4 w-4"
          />
        </span>
        <!-- Use short label on mobile when compact mode is on -->
        <span
          v-if="compact"
          class="hidden sm:inline sm:truncate"
        >
          {{ currentStatus.label }}
        </span>
        <span
          v-if="compact"
          class="sm:hidden"
        >
          {{ currentStatus.shortLabel }}
        </span>
        <!-- Use full label when not in compact mode -->
        <span v-if="!compact">{{ currentStatus.label }}</span>
      </div>
      <ChevronDownIcon
        class="-mr-1 ml-1 h-5 w-5 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        aria-hidden="true"
      />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="fixed z-50 min-w-[200px] rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5
          focus:outline-none"
        :style="{
          left: `${dropdownPosition.x}px`,
          top: `${dropdownPosition.y}px`,
          width: `${dropdownPosition.width}px`,
          transform: 'none'
        }"
        @click.stop
      >
        <div class="py-1">
          <button
            v-for="option in statusOptions"
            :key="option.value"
            class="flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100"
            :class="{ 'bg-gray-50': option.value === props.modelValue }"
            @click="handleStatusChange(option.value)"
          >
            <span
              class="mr-2"
              :class="option.iconClass"
            >
              <component
                :is="option.icon"
                class="h-4 w-4"
              />
            </span>
            {{ option.label }}
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
  .status-select-container {
    isolation: isolate;
  }
</style>
